<template>
    <Layout>
        <PageHeader :title="title" :items="items" />
        <p style="margin-top: -20px; font-size: 12px; position: relative; z-index: 1;">
            <i>CRB Reports are external references to a person's credit history & financial behavior.
                <span class="text-primary fw-medium learn-more-link" @click="$bvModal.show('crb-info-modal')">
                    Learn More
                </span>
            </i>
        </p>

        <div class="row">
            <div class="col-12">
                <div class="card shadow-sm border-0">
                    <div class="card-body">
                        <h5 class="card-title mb-4 fw-bold text-dark">Create New CRB Report</h5>
                        <img src="@/assets/images/crb-gauge-form-image.png" alt="CRB Gauge">
                        <form @submit.prevent="generateReport">

                            <!-- 1. Member Selection -->
                            <div class="mb-4">
                                <label class="form-label fw-bold text-dark">Select Member <span
                                        class="text-danger">*</span></label>
                                <p class="text-muted small mb-2 mt-n1">Select the SACCO member this report is being
                                    generated for.</p>
                                <v-select :options="members" label="name" v-model="selectedMember"
                                    placeholder="Search and select a member..." class="shadow-none">
                                    <template #option="option">
                                        {{ option.name }} - {{ option.member_id }}
                                    </template>
                                </v-select>
                            </div>

                            <hr class="my-4 border-light">

                            <!-- 2. Identification Details -->
                            <div class="row mb-4">
                                <div class="col-md-5">
                                    <label class="form-label fw-bold text-dark">Identification Type <span
                                            class="text-danger">*</span></label>
                                    <select class="form-select shadow-none" v-model="idType" required>
                                        <option value="" disabled>Select ID Type</option>
                                        <option value="National ID">National ID</option>
                                        <option value="Taxation ID">Taxation ID (TIN)</option>
                                        <option value="Passport">Passport</option>
                                        <option value="Driver's License">Driver's License</option>
                                        <option value="Refugee ID">Refugee ID</option>
                                    </select>
                                </div>

                                <div class="col-md-7 mt-3 mt-md-0" v-if="idType">
                                    <label class="form-label fw-bold text-dark">Identification Number <span
                                            class="text-danger">*</span></label>
                                    <div class="input-group">
                                        <input type="text" class="form-control shadow-none" v-model="idValue"
                                            :placeholder="idPlaceholder" required>
                                        <button class="btn btn-primary fw-medium px-4" type="button"
                                            @click="checkPerson" :disabled="!idValue || isVerifying">
                                            <b-spinner v-if="isVerifying" small class="me-1"></b-spinner>
                                            <i v-else class="mdi mdi-account-search me-1"></i> Verify
                                        </button>
                                    </div>
                                    <div class="small mt-1 text-success fw-medium" v-if="isVerified">
                                        <i class="mdi mdi-check-circle"></i> Identity verified
                                    </div>
                                </div>
                            </div>

                            <hr class="my-4 border-light">

                            <!-- 3. Report Type Selection -->
                            <div class="mb-4">
                                <label class="form-label fw-bold text-dark mb-3 d-block">CRB Report Type <span
                                        class="text-danger">*</span></label>

                                <div class="form-check mb-2">
                                    <input class="form-check-input" type="radio" name="reportType" id="typeStandard"
                                        value="STANDARD" v-model="reportType" required>
                                    <label class="form-check-label fw-medium text-dark" for="typeStandard">
                                        Standard Report
                                    </label>
                                </div>

                                <div class="form-check mb-2">
                                    <input class="form-check-input" type="radio" name="reportType" id="typeCompact"
                                        value="COMPACT" v-model="reportType" required>
                                    <label class="form-check-label fw-medium text-dark" for="typeCompact">
                                        Compact Report
                                    </label>
                                </div>

                                <div class="form-check mb-2">
                                    <input class="form-check-input" type="radio" name="reportType" id="typeEnhanced"
                                        value="ENHANCED" v-model="reportType" required>
                                    <label class="form-check-label fw-medium text-dark" for="typeEnhanced">
                                        Enhanced Report
                                    </label>
                                </div>

                                <!-- Dynamic Explanation Box -->
                                <div class="alert alert-light border mt-3 d-flex align-items-start text-muted"
                                    v-if="reportType">
                                    <i class="mdi mdi-information text-primary fs-4 me-2 mt-n1"></i>
                                    <div class="small">
                                        <strong class="text-dark">{{ reportType }} REPORT:</strong> <br>
                                        {{ reportExplanation }}
                                    </div>
                                </div>
                            </div>

                            <!-- Actions -->
                            <div class="text-end mt-5 pt-3">
                                <button type="button" class="btn btn-light px-4 me-2 fw-medium border"
                                    @click="$router.push('/crb-reports')">Cancel</button>
                                <button type="submit" class="btn btn-primary px-4 fw-medium"
                                    :disabled="isGenerating || !isVerified">
                                    <b-spinner v-if="isGenerating" small class="me-1"></b-spinner>
                                    <i v-else class="mdi mdi-file-document-outline me-1"></i> Generate Report
                                </button>
                            </div>

                        </form>
                    </div>
                </div>
            </div>
        </div>

        <CrbInfoModal />

    </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import CrbInfoModal from "./crb_info_modal.vue";
import { memberComputed, memberMethods } from "@/state/helpers";

export default {
    components: {
        Layout,
        PageHeader,
        CrbInfoModal,
        vSelect
    },
    data() {
        return {
            title: "New CRB Report",
            items: [
                { text: "Dashboard", to: "/" },
                { text: "CRB Reports", to: "/crb-reports" },
                { text: "New Report", active: true }
            ],
            selectedMember: null,
            idType: "",
            idValue: "",
            reportType: "",
            isGenerating: false,
            isVerifying: false,
            isVerified: false,
        };
    },
    computed: {
        ...memberComputed,

        idPlaceholder() {
            switch (this.idType) {
                case "National ID": return "e.g CM1234987ABC";
                case "Taxation ID": return "e.g 10183484000";
                case "Passport": return "e.g A1234567";
                case "Driver's License": return "e.g DL987654321";
                case "Refugee ID": return "e.g RFG123456";
                default: return "Enter ID number";
            }
        },

        reportExplanation() {
            switch (this.reportType) {
                case "STANDARD":
                    return "Provides a sequential, chronological history of the applicant's credit applications, enquiries, and bounced cheques without sector categorization. Recommended for general, everyday underwriting.";
                case "COMPACT":
                    return "A quick, structured summary of active debts, arrears, and account balances organized by lending sector. Ideal for fast-tracked, low-value, or emergency mobile loans.";
                case "ENHANCED":
                    return "An exhaustive, deep-dive analysis grouping debts by sector (Banks, MDIs, etc.) while detailing employment, guarantor liabilities, and full financial history. Best for high-value loans and credit committee reviews.";
                default:
                    return "";
            }
        }
    },
    watch: {
        idValue() {
            this.isVerified = false;
        }
    },
    async mounted() {
        await this.fetchMembers();
    },
    methods: {
        ...memberMethods,

        checkPerson() {
            this.isVerifying = true;
            this.$toast.info(`Verifying ${this.idType} with Metropol...`);

            setTimeout(() => {
                this.isVerifying = false;
                this.isVerified = true;
                this.$toast.success("Identity verified successfully. You may proceed.");
            }, 1500);
        },

        generateReport() {
            if (!this.selectedMember) {
                this.$toast.error("Please select a SACCO member.");
                return;
            }
            if (!this.isVerified) {
                this.$toast.error("Please verify the identification number first.");
                return;
            }

            this.isGenerating = true;

            setTimeout(() => {
                this.isGenerating = false;
                this.$toast.success(`${this.reportType} report generated successfully!`);
                this.$router.push('/crb-reports');
            }, 2000);
        }
    }
};
</script>

<style scoped>
.form-check-input:checked {
    background-color: #556ee6;
    border-color: #556ee6;
}

.learn-more-link {
    cursor: pointer;
    text-decoration: none;
    transition: text-decoration 0.2s ease-in-out;
}

.learn-more-link:hover {
    text-decoration: underline !important;
}

/* Make v-select match standard bootstrap inputs */
:deep(.v-select .vs__dropdown-toggle) {
    border-color: #ced4da;
    border-radius: 0.25rem;
    padding: 0.2rem 0;
}
</style>